/**
 * 🧱 Refactor BT Phase 3 — Étape 2 : section « SectionLavagePression » d'EditerBonTravailDialog.jsx.
 * JSX déplacé TEL QUEL (ex-L1806-1965, dédenté de 10) ; variables lues via `useBTForm()`
 * (BTFormProvider enrichi par `extra` dans le composant parent). DOM, data-testid, classes, textes inchangés.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ToursEchafaudsConfig } from '../../ToursEchafaudsConfig';
import { useBTForm } from '../../BTFormContext';

export function SectionLavagePression() {
  const {
    bon,
    form,
    isExt,
    remorqueEchafaudsId,
    setForm,
    setShowListeChargement,
    type,
    typesEchafauds,
  } = useBTForm();
  return (
    <>
      {/* Lavage à pression (extérieur seulement) */}
      {isExt && (
        <Card className="border-blue-200">
          <CardContent className="pt-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="lavage-pression-toggle">
              <input
                type="checkbox"
                checked={form.lavage_pression_a_faire}
                onChange={(e) => setForm(f => ({ ...f, lavage_pression_a_faire: e.target.checked }))}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-sm">Lavage à pression à faire</span>
                <p className="text-xs text-slate-500">Ce projet nécessite un lavage à pression avant les travaux</p>
              </div>
            </label>
            {form.lavage_pression_a_faire && (
              <div className="mt-3 ml-7 space-y-3">
                {/* ── Nacelle (simple, inchangé) ── */}
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all">
                  <input type="checkbox" checked={form.nacelle_lavage_requise || false}
                    onChange={e => setForm(f => ({ ...f, nacelle_lavage_requise: e.target.checked, nacelle_retour: e.target.checked ? (f.nacelle_retour || 'bureau') : null }))}
                    className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400" data-testid="nacelle-lavage-requise" />
                  <span className="text-sm font-medium text-cyan-800">🏗 🏗️ Nacelle requise pour le lavage</span>
                </label>
                {form.nacelle_lavage_requise && (
                  <div className="ml-6 space-y-3 bg-cyan-50/50 border border-cyan-100 rounded-lg p-3">
                    {/* ✅ Nacelle assignée automatiquement */}
                    <div className="flex items-center gap-2 p-2 bg-white border border-cyan-200 rounded-md">
                      <span className="text-sm font-medium text-cyan-800">
                        🏗️ Nacelle assignée : <span className="font-semibold">{form.nacelle_lavage_nom || 'Aucune disponible'}</span>
                      </span>
                    </div>
              
                    {/* Options de retour */}
                    <div className="flex flex-col gap-1.5 pt-2 border-t border-cyan-200/50">
                      <p className="text-xs font-medium text-cyan-700 mb-1">Après le lavage, la nacelle :</p>
                      {[
                        { val: 'bureau', label: '🔙 Ramener la nacelle au bureau' },
                        { val: 'client', label: '📍 Laisser la nacelle chez le client' },
                      ].map(opt => (
                        <label key={opt.val} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input type="radio" name="nacelle_retour" value={opt.val}
                            checked={form.nacelle_retour === opt.val}
                            onChange={() => setForm(f => ({ ...f, nacelle_retour: opt.val }))}
                            className="text-cyan-600" />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Échafauds ── */}
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all">
                  <input type="checkbox" checked={form.echafauds_a_monter || false}
                    onChange={e => setForm(f => ({ ...f, echafauds_a_monter: e.target.checked, echafauds_remorque_retour: e.target.checked ? (f.echafauds_remorque_retour || 'bureau') : null }))}
                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-400" data-testid="echafauds-a-monter" />
                  <span className="text-sm font-medium text-orange-800">🔧 Échafauds à monter</span>
                </label>
                {form.echafauds_a_monter && (
                  <div className="ml-6 flex flex-col gap-2 bg-orange-50/50 border border-orange-100 rounded-lg p-3">
                    {/* ✅ NOUVEAU SYSTÈME : Configuration des tours par zone */}
                    <ToursEchafaudsConfig
                      zones={form.zones || []}
                      typesEtages={typesEchafauds}
                      remorqueId={remorqueEchafaudsId}
                      value={form.echafauds_tours_config || []}
                      onChange={(newConfig) => setForm(f => ({ ...f, echafauds_tours_config: newConfig }))}
                    />

                    {/* 🆕 Bouton Liste de chargement (prêt pour futur entrepôt) */}
                    {(form.echafauds_tours_config || []).length > 0 && bon?.id && (
                      <div className="border-t border-orange-200 pt-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowListeChargement(true)}
                          className="h-8 text-xs border-orange-400 text-orange-700 hover:bg-orange-100 w-full"
                          data-testid="btn-liste-chargement"
                        >
                          📋 Voir la liste de chargement
                        </Button>
                        <p className="text-[10px] text-slate-500 mt-1 text-center italic">
                          (feuille à cocher pour l'équipe en entrepôt)
                        </p>
                      </div>
                    )}
              
                    {/* Remorque retour */}
                    <div className="border-t border-orange-200 pt-2 mt-1">
                      <p className="text-xs font-medium text-orange-700 mb-1.5">La remorque d'échafauds :</p>
                      {[
                        { val: 'bureau', label: '🔙 Rapporter la remorque au bureau' },
                        { val: 'client', label: '📍 Laisser la remorque sur place chez le client' },
                      ].map(opt => (
                        <label key={opt.val} className="flex items-center gap-2 cursor-pointer text-sm mb-1">
                          <input type="radio" name="echafauds_remorque_retour" value={opt.val}
                            checked={form.echafauds_remorque_retour === opt.val}
                            onChange={() => setForm(f => ({ ...f, echafauds_remorque_retour: opt.val }))}
                            className="text-orange-600" />
                          {opt.label}
                        </label>
                      ))}
                      <p className="text-xs text-orange-600 italic mt-2">💡 La date de démontage se définit dans la section "Équipements de location" ci-dessous.</p>
                    </div>
                  </div>
                )}

                {/* ── 🆕 Nettoyer à l'échelle ── */}
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all">
                  <input type="checkbox" checked={form.nettoyer_echelle || false}
                    onChange={e => setForm(f => ({
                      ...f,
                      nettoyer_echelle: e.target.checked,
                      nettoyer_echelle_type_pieds: e.target.checked ? (f.nettoyer_echelle_type_pieds || '24') : null,
                    }))}
                    className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-400" data-testid="nettoyer-echelle" />
                  <span className="text-sm font-medium text-purple-800">🪜 Nettoyer à l'échelle</span>
                </label>
                {form.nettoyer_echelle && (
                  <div className="ml-6 bg-purple-50/50 border border-purple-100 rounded-lg p-3">
                    <p className="text-xs font-medium text-purple-700 mb-1.5">Type d'échelle :</p>
                    <div className="flex flex-col gap-1.5">
                      {[
                        { val: '24', label: '🪜 Échelle 24 pieds' },
                        { val: '32', label: '🪜 Échelle 32 pieds' },
                      ].map(opt => (
                        <label key={opt.val} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input type="radio" name="nettoyer_echelle_type_pieds" value={opt.val}
                            checked={form.nettoyer_echelle_type_pieds === opt.val}
                            onChange={() => setForm(f => ({ ...f, nettoyer_echelle_type_pieds: opt.val }))}
                            className="text-purple-600"
                            data-testid={`echelle-pieds-${opt.val}`} />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes lavage */}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Notes pour l'équipe de lavage (optionnel)</label>
                  <textarea value={form.notes_lavage_pression}
                    onChange={(e) => setForm(f => ({ ...f, notes_lavage_pression: e.target.value }))}
                    rows={2} placeholder="Ex: Monter l'échafaud sur la zone #8 pendant le lavage..."
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-blue-50/30"
                    data-testid="notes-lavage-pression" />
                </div>
                <p className="text-xs text-blue-500 italic">
                  La date d'échéance de cette note sera automatiquement définie par la Route de lavage planifiée.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </>
  );
}
